import type ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';

type MonitoredState = Pick<ProcessRequestExecutionState, 'requestId' | 'isFinished'>;

// 'apply': take the poll result. 'lookUp': read the held request by id. 'wait': a lookup of the held request runs.
export type PollAction = { type: 'apply' } | { type: 'lookUp', requestId: number } | { type: 'wait' };

/**
 * What the monitor does with a poll result (ROO2-233). The latest-active query returns a finished request for 2 seconds
 * only, and a hidden browser tab does not poll. A null poll for a running request can thus hide its end, so the monitor
 * reads that request by id instead of dropping it.
 */
export const pollAction = (
	held: MonitoredState | null,
	polled: MonitoredState | null,
	lookupRequestId: number | undefined,
): PollAction => {
	if (polled || !held || held.isFinished) return { type: 'apply' };
	return held.requestId === lookupRequestId ? { type: 'wait' } : { type: 'lookUp', requestId: held.requestId };
};

/**
 * The monitored state after the lookup of a request by id. A finished result replaces the request; a failed or
 * unfinished lookup clears it. A later or already finished request keeps its own state.
 */
export const lookedUpState = <T extends MonitoredState>(held: T | null, requestId: number, result: T | null): T | null => {
	if (!held || held.requestId !== requestId || held.isFinished) return held;
	return result?.isFinished === true ? result : null;
};
